// Advanced chart component with responsive design and accessibility

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart
} from 'recharts';
import { format, parseISO, isValid } from 'date-fns';

interface TrendChartProps {
  data: Array<{ [key: string]: any }>;
  xAxisKey: string;
  yAxisKey: string;
  height?: number;
  color?: string;
  secondaryYAxisKey?: string;
  secondaryColor?: string;
  formatter?: (value: number) => string;
  dateFormatter?: (date: string) => string;
  showGrid?: boolean;
  showArea?: boolean;
  showReferenceLine?: boolean;
  referenceValue?: number;
  loading?: boolean;
  error?: string;
  className?: string;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  xAxisKey,
  yAxisKey,
  height = 300,
  color = '#3B82F6',
  secondaryYAxisKey,
  secondaryColor = '#10B981',
  formatter = (value: number) => value.toLocaleString(),
  dateFormatter,
  showGrid = true,
  showArea = false,
  showReferenceLine = false,
  referenceValue,
  loading = false,
  error,
  className = ''
}) => {
  // Memoize processed data for performance
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map(item => ({
      ...item,
      // Format dates for display
      [xAxisKey]: dateFormatter ? dateFormatter(item[xAxisKey]) : item[xAxisKey],
      // Ensure numeric values
      [yAxisKey]: typeof item[yAxisKey] === 'number' ? item[yAxisKey] : parseFloat(item[yAxisKey]) || 0,
      ...(secondaryYAxisKey && {
        [secondaryYAxisKey]: typeof item[secondaryYAxisKey] === 'number'
          ? item[secondaryYAxisKey]
          : parseFloat(item[secondaryYAxisKey]) || 0
      })
    }));
  }, [data, xAxisKey, yAxisKey, secondaryYAxisKey, dateFormatter]);

  // Calculate reference line value if not provided
  const calculatedReferenceValue = useMemo(() => {
    if (referenceValue !== undefined) return referenceValue;
    if (!showReferenceLine || processedData.length === 0) return undefined;

    // Use average as reference line
    const values = processedData.map(item => item[yAxisKey]).filter(v => typeof v === 'number');
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }, [referenceValue, showReferenceLine, processedData, yAxisKey]);

  // Custom tooltip component
  const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center space-x-2 mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-600">{entry.dataKey}:</span>
            <span className="text-sm font-medium text-gray-900">
              {formatter(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Format X-axis dates
  const formatXAxisDate = (tickItem: string) => {
    try {
      const date = parseISO(tickItem);
      if (isValid(date)) {
        return format(date, 'MMM dd');
      }
    } catch {
      // If not a valid date, return as is
    }
    return tickItem;
  };

  if (loading) {
    return <ChartLoadingSkeleton height={height} className={className} />;
  }

  if (error) {
    return <ChartError message={error} height={height} className={className} />;
  }

  if (!processedData || processedData.length === 0) {
    return <EmptyChart height={height} className={className} />;
  }

  const ChartComponent = showArea ? ComposedChart : LineChart;

  return (
    <div className={`trend-chart ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#E5E7EB"
              horizontal={true}
              vertical={false}
            />
          )}

          <XAxis
            dataKey={xAxisKey}
            stroke="#6B7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatXAxisDate}
            interval="preserveStartEnd"
          />

          <YAxis
            stroke="#6B7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatter}
            width={80}
          />

          {/* Secondary Y-axis if provided */}
          {secondaryYAxisKey && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatter}
              width={80}
            />
          )}

          <Tooltip content={<CustomTooltip />} />

          {/* Reference line */}
          {showReferenceLine && calculatedReferenceValue !== undefined && (
            <ReferenceLine
              y={calculatedReferenceValue}
              stroke="#9CA3AF"
              strokeDasharray="5 5"
              label={{
                value: `Avg: ${formatter(calculatedReferenceValue)}`,
                position: "insideTopRight"
              }}
            />
          )}

          {/* Area fill if enabled */}
          {showArea && (
            <Area
              type="monotone"
              dataKey={yAxisKey}
              stroke={color}
              fillOpacity={0.1}
              fill={color}
            />
          )}

          {/* Primary line */}
          <Line
            type="monotone"
            dataKey={yAxisKey}
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{
              r: 4,
              stroke: color,
              strokeWidth: 2,
              fill: '#FFFFFF'
            }}
          />

          {/* Secondary line if provided */}
          {secondaryYAxisKey && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey={secondaryYAxisKey}
              stroke={secondaryColor}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 4,
                stroke: secondaryColor,
                strokeWidth: 2,
                fill: '#FFFFFF'
              }}
            />
          )}
        </ChartComponent>
      </ResponsiveContainer>

      {/* Chart legend */}
      <ChartLegend
        items={[
          { label: yAxisKey, color },
          ...(secondaryYAxisKey ? [{ label: secondaryYAxisKey, color: secondaryColor }] : [])
        ]}
      />
    </div>
  );
};

// Chart legend component
interface ChartLegendProps {
  items: Array<{ label: string; color: string }>;
}

const ChartLegend: React.FC<ChartLegendProps> = ({ items }) => (
  <div className="flex items-center justify-center space-x-6 mt-4">
    {items.map((item, index) => (
      <div key={index} className="flex items-center space-x-2">
        <div
          className="w-3 h-0.5 rounded"
          style={{ backgroundColor: item.color }}
        />
        <span className="text-sm text-gray-600 capitalize">
          {item.label.replace(/([A-Z])/g, ' $1').trim()}
        </span>
      </div>
    ))}
  </div>
);

// Loading skeleton
interface ChartLoadingSkeletonProps {
  height: number;
  className?: string;
}

const ChartLoadingSkeleton: React.FC<ChartLoadingSkeletonProps> = ({ height, className }) => (
  <div
    className={`animate-pulse bg-gray-100 rounded flex items-center justify-center ${className}`}
    style={{ height }}
  >
    <div className="text-gray-400">Loading chart...</div>
  </div>
);

// Error state
interface ChartErrorProps {
  message: string;
  height: number;
  className?: string;
}

const ChartError: React.FC<ChartErrorProps> = ({ message, height, className }) => (
  <div
    className={`bg-red-50 border border-red-200 rounded flex items-center justify-center ${className}`}
    style={{ height }}
  >
    <div className="text-center">
      <div className="text-red-600 font-medium">Failed to load chart</div>
      <div className="text-red-500 text-sm mt-1">{message}</div>
    </div>
  </div>
);

// Empty state
interface EmptyChartProps {
  height: number;
  className?: string;
}

const EmptyChart: React.FC<EmptyChartProps> = ({ height, className }) => (
  <div
    className={`bg-gray-50 border border-gray-200 rounded flex items-center justify-center ${className}`}
    style={{ height }}
  >
    <div className="text-center">
      <div className="text-gray-500 font-medium">No data available</div>
      <div className="text-gray-400 text-sm mt-1">Check your filters or data source</div>
    </div>
  </div>
);

// Specialized chart variants
export const RevenueChart: React.FC<Omit<TrendChartProps, 'formatter'>> = (props) => (
  <TrendChart
    {...props}
    formatter={(value: number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(value)
    }
    color="#10B981"
    showArea={true}
  />
);

export const ConversionChart: React.FC<Omit<TrendChartProps, 'formatter'>> = (props) => (
  <TrendChart
    {...props}
    formatter={(value: number) => `${value.toFixed(1)}%`}
    color="#F59E0B"
    showReferenceLine={true}
  />
);

export const VolumeChart: React.FC<TrendChartProps> = (props) => (
  <TrendChart
    {...props}
    color="#8B5CF6"
    showGrid={true}
  />
);

export default TrendChart;